import { FC, ReactElement } from "react";
import { Box, Button, FormControl, Grid, MenuItem, Paper, Select, Stack, TextField, Typography } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { usPorts } from "@/constants/port-coordinates";
import { tokens } from "@/tokens";

const getPortLabel = (portCode: string) => {
  const port = usPorts.find(p => p.portCode === portCode);
  return port ? `${portCode.slice(0, 4)} - ${port.city}` : portCode;
};

const getPortStats = (portCode: string, entries: IFTZEntry[], sortType: FTZSortType) => {
  if (!entries || entries.length === 0) return '0.00%';
  
  let total = 0;
  let portTotal = 0;

  switch (sortType) {
    case 'mpf':
      total = entries.reduce((sum, entry) => sum + entry.mpf, 0);
      portTotal = entries
        .filter(entry => entry.portOfEntry === portCode)
        .reduce((sum, entry) => sum + entry.mpf, 0);
      break;
    case 'duties':
      total = entries.reduce((sum, entry) => sum + entry.totalDuties, 0);
      portTotal = entries
        .filter(entry => entry.portOfEntry === portCode)
        .reduce((sum, entry) => sum + entry.totalDuties, 0);
      break;
    default: // 'lines'
      total = entries.reduce((sum, entry) => sum + entry.lineCount, 0);
      portTotal = entries
        .filter(entry => entry.portOfEntry === portCode)
        .reduce((sum, entry) => sum + entry.lineCount, 0);
  }
  
  const percentage = total > 0 ? (Math.round((portTotal / total) * 10000) / 100).toFixed(2) : '0.00';
  return `${percentage}%`;
};

const sortPorts = (ports: string[], entries: IFTZEntry[], sortType: FTZSortType): string[] => {
  if (!ports || ports.length === 0 || !entries || entries.length === 0) return [];
  
  return [...ports].sort((a, b) => {
    let aValue = 0, bValue = 0;
    
    switch (sortType) {
      case 'mpf':
        aValue = entries.filter(e => e.portOfEntry === a).reduce((sum, e) => sum + e.mpf, 0);
        bValue = entries.filter(e => e.portOfEntry === b).reduce((sum, e) => sum + e.mpf, 0);
        break;
      case 'duties':
        aValue = entries.filter(e => e.portOfEntry === a).reduce((sum, e) => sum + e.totalDuties, 0);
        bValue = entries.filter(e => e.portOfEntry === b).reduce((sum, e) => sum + e.totalDuties, 0);
        break;
      default:
        aValue = entries.filter(e => e.portOfEntry === a).reduce((sum, e) => sum + e.lineCount, 0);
        bValue = entries.filter(e => e.portOfEntry === b).reduce((sum, e) => sum + e.lineCount, 0);
    }
    
    return bValue - aValue;
  });
};

const Filters: FC<IFTZFilterProps> = ({ 
  filters, 
  onFiltersChange, 
  entries,
  currentSort,
  onSortChange,
  totalEntries
}): ReactElement => {
  const handlePortChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    onFiltersChange({
      ...filters,
      selectedPorts: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleRangeChange = (field: keyof Omit<IFTZFilters, 'selectedPorts'>, subField: 'min' | 'max', value: number) => {
    onFiltersChange({
      ...filters,
      [field]: { ...filters[field as keyof IFTZFilters], [subField]: value }
    });
  };

  const handleResetFilters = () => {
    if (!entries || entries.length === 0) {
      onFiltersChange({
        selectedPorts: [],
        lineRange: { min: 0, max: 0 },
        dutySpendRange: { min: 0, max: 0 },
        mpfSpendRange: { min: 0, max: 0 },
      });
      return;
    }
    
    onFiltersChange({
      selectedPorts: [...new Set(entries.map((entry: IFTZEntry) => entry.portOfEntry))],
      lineRange: { min: 0, max: Math.max(...entries.map((e: IFTZEntry) => e.lineCount)) },
      dutySpendRange: { min: 0, max: Math.max(...entries.map((e: IFTZEntry) => e.totalDuties)) },
      mpfSpendRange: { min: 0, max: Math.max(...entries.map((e: IFTZEntry) => e.mpf)) },
    });
  };

  const allPorts = [...new Set(entries?.map((entry: IFTZEntry) => entry.portOfEntry) || [])];
  const sortedPorts = sortPorts(allPorts as string[], entries || [], currentSort);

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Typography variant="body2" gutterBottom>Ports</Typography>
          <FormControl fullWidth size="small">
            <Select
              value={filters.selectedPorts}
              onChange={handlePortChange}
              multiple
              renderValue={(selected) => {
                if (!selected || selected.length === 0) return "No Ports Selected";
                if (selected.length === allPorts.length) return "All Ports Selected";
                return selected
                  .filter(value => value && typeof value === 'string')
                  .map(value => value.slice(0, 4))
                  .join(", ");
              }}
            >
              <Box sx={{ 
                position: 'sticky', 
                top: 0, 
                bgcolor: tokens.bg.surface,
                zIndex: 1,
                borderBottom: '1px solid',
                borderColor: tokens.border.default,
                p: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>
                  Sort by:
                </Typography>
                <Button
                  size="small"
                  variant={currentSort === 'lines' ? 'contained' : 'outlined'}
                  onClick={() => onSortChange('lines')}
                >
                  Lines
                </Button>
                <Button
                  size="small"
                  variant={currentSort === 'mpf' ? 'contained' : 'outlined'}
                  onClick={() => onSortChange('mpf')}
                >
                  MPF
                </Button>
                <Button
                  size="small"
                  variant={currentSort === 'duties' ? 'contained' : 'outlined'}
                  onClick={() => onSortChange('duties')}
                >
                  Duties
                </Button>
              </Box>
              {sortedPorts.map(port => (
                <MenuItem value={port} key={port}>
                  {getPortLabel(port)} ({getPortStats(port, entries, currentSort)} of imports)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant="body2" gutterBottom>Lines Range</Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  type="number"
                  value={filters.lineRange.min}
                  onChange={(e) => handleRangeChange('lineRange', 'min', Number(e.target.value))}
                  placeholder="Min"
                  sx={{ width: '50%' }}
                  InputProps={{
                    sx: { '& input': { textAlign: 'center' } }
                  }}
                />
                <TextField
                  size="small"
                  type="number"
                  value={filters.lineRange.max}
                  onChange={(e) => handleRangeChange('lineRange', 'max', Number(e.target.value))}
                  placeholder="Max"
                  sx={{ width: '50%' }}
                  InputProps={{
                    sx: { '& input': { textAlign: 'center' } }
                  }}
                />
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                <Typography variant="caption" sx={{ width: '50%', textAlign: 'center' }}>Min</Typography>
                <Typography variant="caption" sx={{ width: '50%', textAlign: 'center' }}>Max</Typography>
              </Stack>
            </Grid>

            <Grid item xs={4}>
              <Typography variant="body2" gutterBottom>Duty Range</Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  type="number"
                  value={filters.dutySpendRange.min}
                  onChange={(e) => handleRangeChange('dutySpendRange', 'min', Number(e.target.value))}
                  placeholder="Min"
                  sx={{ width: '50%' }}
                  InputProps={{
                    sx: { '& input': { textAlign: 'center' } }
                  }}
                />
                <TextField
                  size="small"
                  type="number"
                  value={filters.dutySpendRange.max}
                  onChange={(e) => handleRangeChange('dutySpendRange', 'max', Number(e.target.value))}
                  placeholder="Max"
                  sx={{ width: '50%' }}
                  InputProps={{
                    sx: { '& input': { textAlign: 'center' } }
                  }}
                />
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                <Typography variant="caption" sx={{ width: '50%', textAlign: 'center' }}>Min</Typography>
                <Typography variant="caption" sx={{ width: '50%', textAlign: 'center' }}>Max</Typography>
              </Stack>
            </Grid>

            <Grid item xs={4}>
              <Typography variant="body2" gutterBottom>MPF Range</Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  type="number"
                  value={filters.mpfSpendRange.min}
                  onChange={(e) => handleRangeChange('mpfSpendRange', 'min', Number(e.target.value))}
                  placeholder="Min"
                  sx={{ width: '50%' }}
                  InputProps={{
                    sx: { '& input': { textAlign: 'center' } }
                  }}
                />
                <TextField
                  size="small"
                  type="number"
                  value={filters.mpfSpendRange.max}
                  onChange={(e) => handleRangeChange('mpfSpendRange', 'max', Number(e.target.value))}
                  placeholder="Max"
                  sx={{ width: '50%' }}
                  InputProps={{
                    sx: { '& input': { textAlign: 'center' } }
                  }}
                />
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                <Typography variant="caption" sx={{ width: '50%', textAlign: 'center' }}>Min</Typography>
                <Typography variant="caption" sx={{ width: '50%', textAlign: 'center' }}>Max</Typography>
              </Stack>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: -1 }}>
          <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
            # of Entry Summaries: {totalEntries.toLocaleString('en-US')}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleResetFilters}
            size="small"
          >
            Reset All Filters
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default Filters; 